// src/pages/finance-portal/vendors/VendorForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, Divider, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS_OPTIONS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const PAYMENT_METHOD_OPTIONS = [
    { value: 'ACH', label: 'ACH' },
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE', label: 'Wire Transfer' },
    { value: 'VIRTUAL_CARD', label: 'Virtual Card' },
];

const W9_STATUS_OPTIONS = [
    { value: 'NOT_RECEIVED', label: 'Not Received' },
    { value: 'REQUESTED', label: 'Requested' },
    { value: 'RECEIVED', label: 'Received' },
];

const emptyAddress = { street: '', city: '', state: '', zip: '', country: '' };

const initialFormData = {
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    website: '',
    payment_terms: 'NET_30',
    preferred_payment_method: 'ACH',
    currency: 'USD',
    default_gl_account_id: null,
    tax_id: '',
    w9_status: 'NOT_RECEIVED',
    billing_address: { ...emptyAddress },
    notes: '',
    tags: '',
};

export default function VendorForm({ open, onClose, onSaved, vendor }) {
    const { enqueueSnackbar } = useSnackbar();
    const [formData, setFormData] = useState({ ...initialFormData });
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    // GL Account autocomplete
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);
    const [selectedGlAccount, setSelectedGlAccount] = useState(null);

    const isEdit = !!vendor;

    // Fetch GL accounts on mount
    const fetchGlAccounts = useCallback(async () => {
        setGlLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) {
                setGlAccounts(res.data.data || []);
            }
        } catch (err) {
            // silent
        } finally {
            setGlLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchGlAccounts();
        }
    }, [open, fetchGlAccounts]);

    useEffect(() => {
        if (open) {
            setError(null);
            if (vendor) {
                setFormData({
                    company_name: vendor.company_name || '',
                    first_name: vendor.first_name || '',
                    last_name: vendor.last_name || '',
                    email: vendor.email || '',
                    phone: vendor.phone || '',
                    website: vendor.website || '',
                    payment_terms: vendor.payment_terms || 'NET_30',
                    preferred_payment_method: vendor.preferred_payment_method || 'ACH',
                    currency: vendor.currency || 'USD',
                    default_gl_account_id: vendor.default_gl_account_id || null,
                    tax_id: vendor.tax_id || '',
                    w9_status: vendor.w9_status || 'NOT_RECEIVED',
                    billing_address: vendor.billing_address
                        ? (typeof vendor.billing_address === 'string' ? JSON.parse(vendor.billing_address) : { ...emptyAddress, ...vendor.billing_address })
                        : { ...emptyAddress },
                    notes: vendor.notes || '',
                    tags: Array.isArray(vendor.tags) ? vendor.tags.join(', ') : (vendor.tags || ''),
                });
                // Set selected GL account if vendor has one
                if (vendor.default_gl_account_id && vendor.default_gl_account) {
                    setSelectedGlAccount(vendor.default_gl_account);
                } else {
                    setSelectedGlAccount(null);
                }
            } else {
                setFormData({ ...initialFormData, billing_address: { ...emptyAddress } });
                setSelectedGlAccount(null);
            }
        }
    }, [open, vendor]);

    // Sync selected GL account from loaded accounts list when editing
    useEffect(() => {
        if (isEdit && vendor?.default_gl_account_id && glAccounts.length > 0 && !selectedGlAccount) {
            const found = glAccounts.find(a => a.id === vendor.default_gl_account_id);
            if (found) setSelectedGlAccount(found);
        }
    }, [isEdit, vendor, glAccounts, selectedGlAccount]);

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateAddress = (field, value) => {
        setFormData(prev => ({
            ...prev,
            billing_address: { ...prev.billing_address, [field]: value },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const tagsArray = formData.tags
                ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
                : [];

            const payload = {
                company_name: formData.company_name || null,
                first_name: formData.first_name || null,
                last_name: formData.last_name || null,
                email: formData.email || null,
                phone: formData.phone || null,
                website: formData.website || null,
                payment_terms: formData.payment_terms,
                preferred_payment_method: formData.preferred_payment_method,
                currency: formData.currency,
                default_gl_account_id: selectedGlAccount?.id || null,
                tax_id: formData.tax_id || null,
                w9_status: formData.w9_status,
                billing_address: formData.billing_address,
                notes: formData.notes || null,
                tags: tagsArray,
            };

            if (isEdit) {
                await axiosInstance.put(`/api/finance/vendors/${vendor.id}`, payload);
                enqueueSnackbar('Vendor updated successfully', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/vendors', payload);
                enqueueSnackbar('Vendor created successfully', { variant: 'success' });
            }

            onSaved?.();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save vendor';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEdit ? 'Edit Vendor' : 'New Vendor'}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}

                {/* Basic Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <TextField
                        label="Company Name" size="small" fullWidth
                        value={formData.company_name}
                        onChange={e => updateField('company_name', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="First Name" size="small" fullWidth
                            value={formData.first_name}
                            onChange={e => updateField('first_name', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Last Name" size="small" fullWidth
                            value={formData.last_name}
                            onChange={e => updateField('last_name', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Email" size="small" fullWidth type="email"
                            value={formData.email}
                            onChange={e => updateField('email', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Phone" size="small" fullWidth
                            value={formData.phone}
                            onChange={e => updateField('phone', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                    <TextField
                        label="Website" size="small" fullWidth
                        value={formData.website}
                        onChange={e => updateField('website', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Payment Settings */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Payment Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Payment Terms</InputLabel>
                            <Select
                                value={formData.payment_terms}
                                onChange={e => updateField('payment_terms', e.target.value)}
                                label="Payment Terms"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {PAYMENT_TERMS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Preferred Payment Method</InputLabel>
                            <Select
                                value={formData.preferred_payment_method}
                                onChange={e => updateField('preferred_payment_method', e.target.value)}
                                label="Preferred Payment Method"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {PAYMENT_METHOD_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Currency" size="small" sx={{ minWidth: 100 }}
                            value={formData.currency}
                            onChange={e => updateField('currency', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={glAccounts}
                            getOptionLabel={opt => opt ? `${opt.account_number || ''} - ${opt.name || ''}`.trim() : ''}
                            value={selectedGlAccount}
                            onChange={(_, val) => setSelectedGlAccount(val)}
                            loading={glLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Default GL Account"
                                    InputLabelProps={{ ...params.InputLabelProps, sx: labelSx }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' },
                                        endAdornment: (
                                            <>
                                                {glLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No accounts found"
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Tax Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Tax Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                    <TextField
                        label="Tax ID" size="small" fullWidth
                        value={formData.tax_id}
                        onChange={e => updateField('tax_id', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={labelSx}>W-9 Status</InputLabel>
                        <Select
                            value={formData.w9_status}
                            onChange={e => updateField('w9_status', e.target.value)}
                            label="W-9 Status"
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {W9_STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Billing Address */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Billing Address
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <TextField
                        label="Street" size="small" fullWidth
                        value={formData.billing_address.street}
                        onChange={e => updateAddress('street', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="City" size="small" fullWidth
                            value={formData.billing_address.city}
                            onChange={e => updateAddress('city', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="State" size="small" sx={{ minWidth: 120 }}
                            value={formData.billing_address.state}
                            onChange={e => updateAddress('state', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="ZIP Code" size="small" sx={{ minWidth: 120 }}
                            value={formData.billing_address.zip}
                            onChange={e => updateAddress('zip', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Country" size="small" fullWidth
                            value={formData.billing_address.country}
                            onChange={e => updateAddress('country', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Notes & Tags */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Additional
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        label="Notes" size="small" fullWidth multiline rows={3}
                        value={formData.notes}
                        onChange={e => updateField('notes', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <TextField
                        label="Tags" size="small" fullWidth
                        value={formData.tags}
                        onChange={e => updateField('tags', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        helperText="Comma-separated tags (e.g. Preferred, International, Critical)"
                        FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || (!formData.company_name && !formData.first_name && !formData.last_name)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Vendor'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
